import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function formatError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [authPrefillEmail, setAuthPrefillEmail] = useState("");

  const openAuth = (mode = "login", prefillEmail = "") => { setAuthMode(mode); setAuthPrefillEmail(prefillEmail); setAuthOpen(true); };

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    const run = async () => {
      // Emergent OAuth callback: process session_id from URL fragment first
      if (window.location.hash.includes("session_id=")) {
        const sid = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
        const role = localStorage.getItem("pending_role") || "candidate";
        try {
          const { data } = await axios.post(`${API}/auth/session`, { session_id: sid, role });
          setUser(data.user);
        } catch { /* ignore */ }
        localStorage.removeItem("pending_role");
        window.history.replaceState(null, "", window.location.pathname);
        setLoading(false);
        return;
      }
      await refresh();
      setLoading(false);
    };
    run();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    setUser(data.user);
    return data.user;
  };

  const secretAccess = async (passcode) => {
    // Verifies the passcode + sets the admin session cookie server-side.
    // Does NOT setUser — caller plays the "granted" animation then calls refresh().
    await axios.post(`${API}/auth/secret-access`, { passcode });
    return true;
  };

  const register = async (payload) => {
    const { data } = await axios.post(`${API}/auth/register`, payload);
    setUser(data.user);
    return data.user;
  };

  const loginWithGoogle = (role = "candidate") => {
    localStorage.setItem("pending_role", role);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch { /* ignore */ }
    setUser(null);
  };

  const updateProgress = async (n) => {
    const { data } = await axios.put(`${API}/me/progress`, { journey_progress: n });
    setUser(data);
    return data;
  };

  const completeFirewatch = async () => {
    const { data } = await axios.post(`${API}/me/courses/firewatch/complete`);
    setUser(data);
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, authOpen, authMode, authPrefillEmail, setAuthOpen, openAuth, login, register, loginWithGoogle, logout, updateProgress, completeFirewatch, refresh, secretAccess }}>
      {children}
    </AuthContext.Provider>
  );
}
